import { useEffect, useRef, useState, type CSSProperties } from "react";
import { motion, useReducedMotion, type PanInfo } from "motion/react";

type PhoneMockupProps = {
  className?: string;
  /** Absolute public paths to images, e.g. ["/guide-pics/1a..png", ...]. Defaults to home-pics. */
  screens?: ReadonlyArray<string>;
  /** Optional per-image styles, keyed by screen path. */
  screenStyles?: Record<string, CSSProperties>;
  /** Use a taller aspect ratio (e.g. for portrait-heavy screenshots). Default: 320/640. */
  tall?: boolean;
};

/** Home screen photos in carousel order. */
const DEFAULT_SCREENS = [
  "/home-pics/1.jpg",
  "/home-pics/2.jpg",
  "/home-pics/3.jpg",
  "/home-pics/4.jpg",
  "/home-pics/5.jpg",
];
const AUTO_ADVANCE_MS = 4000;
// Swipe is committed when the user has dragged > 18% of the screen width
// or flicked faster than 400 px/s in a direction.
const SWIPE_DISTANCE_PCT = 0.18;
const SWIPE_VELOCITY_PX_PER_S = 400;

/**
 * iPhone-style frame with a 5-screen carousel of real app screenshots.
 *
 * Interaction model:
 *  - Auto-advances every 4s (loops).
 *  - User can drag/swipe horizontally on the screen — the slides follow the
 *    finger and snap to the nearest neighbour on release.
 *  - Pagination dots below the phone jump directly to a slide.
 *  - Auto-advance pauses while the user hovers, swipes, or has the phone in
 *    focus, then resumes.
 *  - Honors `prefers-reduced-motion` (no float, no auto-advance, instant
 *    crossfade between slides — but manual swiping still works).
 */
export function PhoneMockup({
  className = "",
  screens = DEFAULT_SCREENS,
  screenStyles = {},
  tall = false,
}: PhoneMockupProps) {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const screenRef = useRef<HTMLDivElement>(null);

  // Preload all screens once so swipes / wraps never flash blank.
  useEffect(() => {
    screens.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [screens]);

  // Auto-advance — pauses on hover, drag, or reduced-motion.
  useEffect(() => {
    if (reduce || paused || dragging) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % screens.length);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [reduce, paused, dragging, screens]);

  const goTo = (next: number) => {
    setIndex(next);
  };

  const handleDragEnd = (
    _event: PointerEvent | MouseEvent | TouchEvent,
    info: PanInfo,
  ) => {
    setDragging(false);
    const width = screenRef.current?.offsetWidth ?? 0;
    if (!width) return;

    const distancePct = Math.abs(info.offset.x) / width;
    const flicked = Math.abs(info.velocity.x) > SWIPE_VELOCITY_PX_PER_S;
    const committed = distancePct > SWIPE_DISTANCE_PCT || flicked;

    if (!committed) return;
    if (info.offset.x < 0) {
      setIndex((i) => Math.min(i + 1, screens.length - 1));
    } else {
      setIndex((i) => Math.max(i - 1, 0));
    }
  };

  return (
    <div
      className={`relative flex flex-col items-center ${className}`}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <motion.div
        className="relative"
        animate={reduce ? undefined : { y: [0, -10, 0] }}
        transition={
          reduce
            ? undefined
            : { duration: 6, repeat: Infinity, ease: "easeInOut" }
        }
      >
        {/* Soft pink glow */}
        <div
          aria-hidden="true"
          className="absolute -inset-10 -z-10 rounded-[60px] opacity-70 blur-3xl dark:opacity-80"
          style={{
            background:
              "radial-gradient(closest-side, rgba(227,28,93,0.55), rgba(227,28,93,0))",
          }}
        />

        {/* Phone body */}
        <div
          className="relative w-[280px] sm:w-[320px]"
          style={{ aspectRatio: tall ? "320 / 720" : "320 / 640" }}
          aria-hidden="true"
        >
          {/* Outer frame */}
          <div
            className="absolute inset-0 overflow-hidden shadow-2xl"
            style={{
              borderRadius: tall ? "28px" : "44px",
              background: "linear-gradient(180deg, #1d1d22, #0a0a0c)",
            }}
          >
            {/* Inner black bezel */}
            <div
              className="absolute inset-[4px] overflow-hidden bg-black"
              style={{ borderRadius: tall ? "22px" : "40px" }}
            >
              {/* Screen — swipeable viewport */}
              <div
                ref={screenRef}
                className="absolute inset-0 overflow-hidden bg-black"
              >
                <motion.div
                  className="flex h-full select-none"
                  style={{
                    width: `${screens.length * 100}%`,
                    touchAction: "pan-y",
                    cursor: dragging ? "grabbing" : "grab",
                  }}
                  drag="x"
                  dragConstraints={screenRef}
                  dragElastic={0.15}
                  dragMomentum={false}
                  onDragStart={() => setDragging(true)}
                  onDragEnd={handleDragEnd}
                  animate={{ x: `-${index * (100 / screens.length)}%` }}
                  transition={
                    reduce
                      ? { duration: 0.15 }
                      : {
                          type: "spring",
                          stiffness: 350,
                          damping: 35,
                          mass: 0.5,
                        }
                  }
                >
                  {screens.map((src, i) => (
                    <div
                      key={src}
                      className="h-full shrink-0"
                      style={{
                        width: `${100 / screens.length}%`,
                        padding: tall ? "12px 6px 0" : "12px 6px 0",
                      }}
                    >
                      <img
                        src={src}
                        alt=""
                        className="block h-full w-full"
                        style={{
                          borderRadius: "8px 8px 0 0",
                          objectFit: "cover",
                          objectPosition: "top center",
                          ...screenStyles[src],
                        }}
                        draggable={false}
                        loading={i === 0 ? "eager" : "lazy"}
                        decoding="async"
                      />
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Pagination dots — static (don't float with the phone) */}
      <div
        className="mt-6 flex items-center gap-2"
        aria-label="App screen previews"
      >
        {screens.map((_src, i) => {
          const active = i === index;
          return (
            <button
              key={i}
              type="button"
              aria-label={`Show app screen ${i + 1} of ${screens.length}`}
              aria-current={active ? "true" : undefined}
              onClick={() => goTo(i)}
              className={
                "h-2 rounded-full transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 " +
                (active
                  ? "w-7 bg-brand-500"
                  : "w-2 bg-text-faint/40 hover:bg-text-subtle")
              }
            />
          );
        })}
      </div>
    </div>
  );
}
